/* global analytics */
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import cn from 'classnames'
import truncate from 'lodash/truncate'
import get from 'lodash/get'
import ahoy from 'ahoy.js'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import useLoginModalState from '../../hooks/useLoginModalState'
import { useRestoreSiteFooter } from '../../hooks/useSiteFooter'
import useHideIntercom from '../../hooks/useHideIntercom'
import { useGetBucketListItemsIndexQuery } from '../../hooks/useQueries'
import { fetchListing } from '../../actions/listingActions'
import { openSelectEscape } from '../../components/SelectEscape/reduxSlice'
import { setActionToReplay } from '../../actions/authActions'
import { media, mediaMax } from '../../styles/media'
import { useUtmParams } from '../../utils/utmParams'

import Button from '../../components/display/Button'
import GalleryHeader from '../../components/display/GalleryHeader'
import InlineSpinner from '../../components/display/InlineSpinner'
import InvisibleButton from '../../components/display/InvisibleButton'
import ShareButton from '../../components/display/ShareButton'
import SpinnerContainer from '../../components/display/SpinnerContainer'
import Spinner from '../../components/display/Spinner'
import LockBodyScroll from '../../components/display/LockBodyScroll'
import CloseIcon from '../../components/icons/AirbnbClose'
import Container from '../../components/layout/Container'
import Sticky from '../../components/utils/Sticky'
import Suggestions from '../../search/Suggestions'
import JsonLd from '../../components/utils/JsonLd'
import ReviewsModal from '../../components/reviews/ReviewsModal'
import { isExperience } from '../../components/utils/listingUtils'
import { makeItemKey } from '../../dashboard/MyBucketlists/gql/getBucketListItemsIndexHelpers'
import { zIndex } from '../../layout/Navbar/constants'

import DesktopListingHeader from './Header/DesktopListingHeader'
import MobileActionBar from './Booking/MobileActionBar'
import ListingOverview from './Sections/ListingOverview'
import ListingDetails from './Sections/ListingDetails'
import ListingNotes from './Sections/ListingNotes'
import ListingAmenities from './Sections/ListingAmenities'
import ListingAdventures from './Sections/ListingAdventures'
import ListingLocation from './Sections/ListingLocation'
import ListingStories from './Sections/ListingStories'
import ListingTopMoments from './Sections/ListingTopMoments'
import ListingReviews from './Sections/ListingReviews'
import ListingAuthor from './Sections/ListingAuthor'
import ListingMobileGallery from './Header/ListingMobileGallery'
import BookingForm, { BookingFormWrapper } from './Booking/BookingForm'
import BenefitsBanner from './Onboarding/BenefitsBanner'
import EditListingCard from './Onboarding/EditListingCard'
import {
  Section,
  ListingWrapper,
  ContentWrapper,
  ListingTitle,
} from './Sections/shared'

const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 40px 0;
  background-color: white;
  border-bottom: 1px solid #d1d1d1;

  &.is-sticky {
    padding: 20px 0;
    z-index: 9;
  }

  ${Button} {
    width: 100%;
    white-space: nowrap;
    margin-bottom: 16px;
    .icon {
      font-size: 20px;
    }
    .icon-bucketlist-heart-on,
    .icon-bucketlist-heart-off {
      vertical-align: bottom;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ${media.sm`
    padding: 40px 0;

    ${Button} {
      width: auto;
      margin: 0;
      margin-left: 16px;
      &:first-child {
        margin-left: 0;
      }
    }
  `}

  ${media.lg`
    position: sticky;
    top: 0;
  `}
`

const CloseButton = styled(InvisibleButton)`
  color: ${({ theme }) => theme.brand.forest};
  padding: 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`

const SidebarWrapper = styled.div`
  transition: transform 300ms ease-out;
  position: sticky;
  top: -38px;

  ${mediaMax.lg`
    position: fixed;
    overflow: scroll;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: ${zIndex + 1};

    padding-bottom: 80px;

    transform: translateY(125%);
    background-color: ${({ theme }) => theme.brand.sand};

    &.show-on-mobile {
      transform: translateY(0);
    }
  `}

  &.is-sticky {
    padding-top: 20px;
  }

  ${CloseButton} {
    position: absolute;
    top: 0;
    right: 0;
  }

  ${media.lg`
    padding-top: 56px;
    padding-bottom: 56px;

    ${BookingFormWrapper} {
      max-width: 344px;
      margin-left: auto;
    }

    ${CloseButton} {
      display: none;
    }
  `}
`

const MobileHeaderWrapper = styled.div`
  ${media.md`
    display: none;
  `}
`

const Listing = ({
  dispatch,
  enquiry,
  fetched,
  fetching,
  listing,
  location,
  match,
}) => {
  useRestoreSiteFooter()
  useHideIntercom()

  const [showBookingFormOnMobile, setShowBookingFormOnMobile] = useState(false)
  const [mobileGalleryOpen, setMobileGalleryOpen] = useState(false)
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false)

  const { lg: isDesktop } = useMediaQueryContext()

  const isOnboarding = listing?.onboarding

  const { currentUserSignedIn, currentUserLoading, openLoginModal } =
    useLoginModalState()

  const utmParams = useUtmParams()

  const trackListingViewed = () => {
    if (isOnboarding) {
      analytics.track('Draft Listing Viewed', {
        listing_id: listing.id,
        listing_title: listing.title,
        ...utmParams,
      })
    } else {
      analytics.track('Listing Viewed', {
        listing_id: listing.id,
        listing_title: listing.title,
        ...utmParams,
      })
      ahoy.track('Viewed listing', {
        listing_id: listing.id,
      })
      analytics.track(
        'ViewContent',
        {
          // eslint-disable-line
          content_ids: [listing.id, `listing_${listing.id}`],
          content_name: listing.title,
          content_type: 'product',
          value: listing.price,
          currency: listing.currency,
        },
        {
          integrations: {
            All: false,
            'Facebook Conversions API (Actions)': true,
            'Pinterest Conversions API': true,
            'Tiktok Conversions': true,
          },
        },
      )
    }
  }

  useEffect(() => {
    if (listing?.id != null) {
      trackListingViewed()
    }
  }, [listing?.id])

  const listingFetchTriggered = useRef(false)

  useEffect(() => {
    const pathnameMatchesListing =
      listing?.id && location.pathname.includes(listing.id.toString())

    if (fetching || listingFetchTriggered.current) return

    if (!listing || pathnameMatchesListing !== true) {
      dispatch(fetchListing(match.params.id))

      listingFetchTriggered.current = true
    }
  }, [listing, location, match, fetching, listingFetchTriggered])

  const {
    loading: bucketListItemsIndexLoading,
    data: bucketListItemsIndexData,
  } = useGetBucketListItemsIndexQuery()

  const bucketListItemsIndex =
    bucketListItemsIndexData?.currentUser?.bucketListItemsIndex
  const itemBucketListKey = makeItemKey({
    itemId: listing.id,
    itemType: 'Listing',
  })

  const bucketListIds =
    bucketListItemsIndex && (bucketListItemsIndex[itemBucketListKey] ?? [])
  const isInBucketList = bucketListIds?.length > 0

  const jsonLdData = () => {
    const aggregateRating =
      listing.review_count > 0
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: (listing.review_score * 5).toFixed(2),
              reviewCount: listing.review_count,
            },
          }
        : {}

    const geo =
      listing.listing_type.slug === 'accommodation'
        ? {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: listing.location.lat,
              longitude: listing.location.lng,
            },
          }
        : {}

    let listingType = ['Product']

    if (listing.listing_type.slug === 'accommodation') {
      listingType = ['House', 'Product']
    } else if (listing.listing_type.slug === 'motor-vehicle') {
      listingType = ['Vehicle', 'Product']
    }

    return {
      '@context': 'http://schema.org',
      '@type': listingType,
      offers: {
        businessFunction: {
          '@type': 'BusinessFunction',
          url: 'http://purl.org/goodrelations/v1#LeaseOut',
        },
        price: listing.price,
        priceCurrency: listing.currency?.toLowerCase(),
        availability: 'http://schema.org/InStock',
        condition: 'new',
      },
      name: listing.title,
      brand: 'Riparide',
      description: truncate(listing.description, { length: 232 }),
      ...aggregateRating,
      identifier: listing.id,
      productID: listing.id,
      url: `https://www.riparide.com${listing.url}`,
      image: listing.promo_image.medium,
      price: `${listing.price} aud`,
      availability: 'http://schema.org/InStock',
      numberOfRooms: listing.bedrooms,
      address: {
        '@type': 'PostalAddress',
        addressLocality: listing.city,
        addressRegion: listing.state,
        addressCountry: listing.country,
        postalCode: listing.postcode,
      },
      ...geo,
    }
  }

  const handleBucketListButtonClick = () => {
    const openSelectEscapeAction = openSelectEscape({
      itemId: listing.id,
      itemType: 'Listing',
      source: 'listing_page',
    })

    if (!currentUserSignedIn) {
      openLoginModal()
      dispatch(setActionToReplay(openSelectEscapeAction))
    } else {
      dispatch(openSelectEscapeAction)
    }
  }

  const handleShowBookingFormOnMobile = () => {
    setMobileGalleryOpen(false)
    setShowBookingFormOnMobile(true)
  }

  const handleHideBookingFormOnMobile = () => setShowBookingFormOnMobile(false)

  if (!fetched || fetching) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }

  return (
    <>
      {isOnboarding && (
        <BenefitsBanner
          listingId={listing.id?.toString()}
          marketCode={listing?.market?.code}
          hostFirstName={listing.user.first_name}
        />
      )}

      {listing.review_score > 0 && (
        <ReviewsModal
          reviewType="listing"
          reviewScore={listing.review_score}
          typeId={listing.id}
          isOpen={reviewsModalOpen}
          onRequestClose={() => {
            setReviewsModalOpen(false)
          }}
        />
      )}

      <ListingWrapper>
        {!isDesktop && (showBookingFormOnMobile || mobileGalleryOpen) && (
          <LockBodyScroll />
        )}

        {/* Header */}
        <MobileHeaderWrapper>
          {listing.photos[0] && (
            <GalleryHeader
              backgroundImage={listing.photos[0].image}
              photos={listing.photos}
              taller
              onOpen={() => setMobileGalleryOpen(true)}
            />
          )}
        </MobileHeaderWrapper>

        <DesktopListingHeader photos={listing.photos} />

        {/* Content */}
        <ContentWrapper>
          <>
            <Container overflowHidden={false}>
              <div className="row">
                <div className="col-md-7 col-lg-8">
                  <Section noBorder style={{ paddingBottom: 0 }}>
                    <ListingTitle>{listing.title}</ListingTitle>
                    <ListingOverview
                      listing={listing}
                      showAvailabilityAlerts
                      openReviewModal={() => setReviewsModalOpen(true)}
                    />
                  </Section>

                  <div>
                    <Sticky>
                      {({ isSticky }) => (
                        <ActionBar
                          className={isDesktop && isSticky ? 'is-sticky' : ''}
                        >
                          <Button
                            onClick={handleBucketListButtonClick}
                            disabled={
                              fetching ||
                              bucketListItemsIndexLoading ||
                              currentUserLoading
                            }
                            ghost
                          >
                            {bucketListItemsIndexLoading ? (
                              <InlineSpinner className="icon" />
                            ) : (
                              <i
                                className={cn('icon', {
                                  'icon-bucketlist-heart-off': !isInBucketList,
                                  'icon-bucketlist-heart-on': isInBucketList,
                                })}
                              />
                            )}
                            Bucket List
                          </Button>
                          {listing.giftable && (
                            <Button
                              as="a"
                              href={`/vouchers/create?listing_id=${listing.id}`}
                              ghost
                            >
                              <i className="icon icon icon-gift" />
                              Gift this
                            </Button>
                          )}
                          <ShareButton
                            title={`Check out ${listing.title} on Riparide`}
                            url={`https://www.riparide.com${listing.url}`}
                            source="listing_page"
                          />
                        </ActionBar>
                      )}
                    </Sticky>

                    {!isExperience(listing) && listing.amenities.length > 0 && (
                      <ListingAmenities
                        listing={listing}
                        isDesktop={isDesktop}
                      />
                    )}

                    <ListingDetails listing={listing} />

                    {isOnboarding && (
                      <>
                        <Section noBorder>
                          <EditListingCard />
                        </Section>
                      </>
                    )}

                    {listing.review_count > 0 && (
                      <>
                        <ListingTopMoments
                          moments={listing.fulfilling_moments}
                        />

                        <ListingReviews
                          listing={listing}
                          openReviewModal={() => setReviewsModalOpen(true)}
                        />
                      </>
                    )}

                    <ListingAuthor listing={listing} />

                    {(listing.whats_provided || listing.things_to_note) && (
                      <ListingNotes listing={listing} />
                    )}

                    <ListingAdventures listing={listing} />

                    <ListingLocation listing={listing} />

                    {listing.stories.length > 0 && (
                      <ListingStories listing={listing} />
                    )}
                  </div>
                </div>

                <SidebarWrapper
                  className={`col-md-5 col-lg-4 ${cn({
                    'show-on-mobile': showBookingFormOnMobile,
                  })}`}
                >
                  <BookingForm
                    listingId={listing.id}
                    listingBookable={listing?.bookable}
                    listingMarket={listing?.market}
                    doNotShowPayoutTotal
                  />
                  <CloseButton onClick={handleHideBookingFormOnMobile}>
                    <CloseIcon />
                  </CloseButton>
                </SidebarWrapper>
              </div>
            </Container>
          </>
        </ContentWrapper>

        <Section noBorder>
          <Container>
            <Suggestions listing={listing} />
          </Container>
        </Section>

        <MobileActionBar
          listing={listing}
          onShowBookingForm={handleShowBookingFormOnMobile}
        />
        <JsonLd data={jsonLdData()} />

        {!isDesktop && (
          <ListingMobileGallery
            listingId={listing.id}
            isOpen={mobileGalleryOpen}
            onBook={handleShowBookingFormOnMobile}
            onRequestClose={() => setMobileGalleryOpen(false)}
            total={get(enquiry.data, 'total')}
          />
        )}
      </ListingWrapper>
    </>
  )
}

const filterAdventuresByLocationType = (adventures, location_type) =>
  adventures.filter((a) => a.location_type === location_type)

export default connect(({ listing, enquiry, auth }) => ({
  enquiry,
  fetched: listing.fetched,
  hydrated: listing.hydrated,
  fetching: listing.fetching,
  listing: {
    ...listing.data,
    adventures: {
      on_property: listing.data
        ? filterAdventuresByLocationType(listing.data.adventures, 'on_property')
        : [],
      off_property: listing.data
        ? filterAdventuresByLocationType(
            listing.data.adventures,
            'off_property',
          )
        : [],
    },
    features: listing.features,
    amenities: listing.amenities,
    details: listing.details,
    pricing: listing.pricing,
    fulfilling_moments: listing.fulfilling_moments,
  },
  auth,
}))(Listing)
